// Pure SMS length/segment math for the campaign SMS composer. No React, no IO —
// kept separate so it is unit-testable (the lib has no test runner today, so it
// is exercised via typecheck + the dialog's live counter).
//
// Encoding rules mirror how carriers bill concatenated SMS:
// - GSM-7 (basic + extension charset): 160 chars in a single message, 153 per
//   segment once concatenated. Extension-table chars (€ [ ] { } \ ^ | ~) cost
//   2 septets each.
// - Any char outside the GSM basic/extension tables forces UCS-2: 70 single /
//   67 per concatenated segment, counted in UTF-16 code units.

export type SmsEncoding = 'GSM-7' | 'UCS-2'

export interface SmsSegmentInfo {
  encoding: SmsEncoding
  // Billable units: septets for GSM-7 (extension chars count double), UTF-16
  // code units for UCS-2.
  units: number
  segments: number
  // Capacity of a single-segment message in this encoding (160 or 70).
  singleSegmentLimit: number
  // Per-segment capacity once the message concatenates (153 or 67).
  concatSegmentLimit: number
}

// GSM 03.38 basic character set (incl. space, LF, CR).
const GSM_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'

// GSM 03.38 extension table — each costs an escape + char (2 septets).
const GSM_EXTENDED = '€[]{}\\^|~\f'

const GSM_BASIC_SET = new Set(GSM_BASIC)
const GSM_EXTENDED_SET = new Set(GSM_EXTENDED)

export const smsSegmentInfo = (body: string): SmsSegmentInfo => {
  let gsm = true
  let septets = 0
  for (const ch of body) {
    if (GSM_BASIC_SET.has(ch)) {
      septets += 1
    } else if (GSM_EXTENDED_SET.has(ch)) {
      septets += 2
    } else {
      gsm = false
      break
    }
  }

  if (gsm) {
    return {
      encoding: 'GSM-7',
      units: septets,
      segments:
        septets === 0 ? 0 : septets <= 160 ? 1 : Math.ceil(septets / 153),
      singleSegmentLimit: 160,
      concatSegmentLimit: 153,
    }
  }

  // UCS-2 is billed per UTF-16 code unit (surrogate pairs cost 2).
  const codeUnits = body.length
  return {
    encoding: 'UCS-2',
    units: codeUnits,
    segments:
      codeUnits === 0 ? 0 : codeUnits <= 70 ? 1 : Math.ceil(codeUnits / 67),
    singleSegmentLimit: 70,
    concatSegmentLimit: 67,
  }
}
